/**
 * 상회칠판(·별채대화)의 카드 (DESIGN_SPEC 6장, 시안 03 README 5.5·5.8, D15·D23).
 *
 * - 매일 콘텐츠: 운세 · 니체의 한 문장 · 오늘의 한 수 · 체크인. 담당 멤버가 올리고 다른 멤버 한 줄 댓글,
 *   맨 아래 리액션 줄(공감·위로·응원)
 * - 내 고민: 카드 전체가 버튼. 답 본문은 고민 스레드에서
 * 하루 글과 환영 묶음은 v1.2 에서 별채대화로 옮겼습니다(C-2, D23)
 *
 * 근거 칩은 매일 콘텐츠 글 본문(니체의 한 문장, 오늘의 한 수)에만, 서버가 근거 DB 에 있다고 확인한 항목에만
 * 붙습니다 (CLAUDE.md). 멤버 댓글·답 같은 대화에는 붙이지 않습니다(D18).
 */
import { useState } from 'react';
import { HandHeart, Heart, Sparkles } from 'lucide-react';
import { getMember } from '@config/members/index.js';
import { DAILY_KINDS, NO_INVEST_NOTICE, REACTIONS, WEEKLY_LETTER } from '@config/daily/index.js';
import { withJosa } from '@shared/korean.js';
import { Avatar } from '../../components/Avatar.jsx';
import { Card } from '../../components/Card.jsx';
import { EvidenceChip, PostTag } from '../../components/Chips.jsx';
import { Button } from '../../components/Button.jsx';
import { TypingIndicator } from '../../components/TypingIndicator.jsx';
import { formatClock, nextResponderOf, splitComments, worryCardStatus } from './boardFeed.js';

const REACTION_ICONS = { empathy: Heart, comfort: HandHeart, cheer: Sparkles };

function memberName(memberKey) {
    return getMember(memberKey)?.name ?? '';
}

function initialOf(name) {
    return Array.from(name ?? '')[0] ?? '';
}

// ── 공통 조각 ───────────────────────────────────────────────

/**
 * 멤버 댓글 (DESIGN_SPEC 6장). 위에 hairline, 아바타 24 + 이름 + 본문(부리 15/26). 근거 칩은 붙이지 않습니다(D18).
 * 2개까지 보이고, 더 있으면 「댓글 n개 더 보기」. 쓰는 중이면 마지막에 작성 중 줄.
 * @param {{ key: string|number, memberKey: string, body: string }[]} comments
 */
export function MemberComments({ comments, typingMemberKey = null }) {
    const [expanded, setExpanded] = useState(false);
    const { visible, hiddenCount } = splitComments(comments, expanded);
    if (!comments.length && !typingMemberKey) {
        return null;
    }
    return (
        <div className="flex flex-col gap-3 border-t border-hairline pt-3">
            {visible.map((comment) => (
                <div key={comment.key} className="flex items-start gap-2">
                    <Avatar memberKey={comment.memberKey} size={24} />
                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                        <span className="text-[13px] font-semibold">{memberName(comment.memberKey)}</span>
                        <p className="m-0 font-serif text-[15px] leading-[26px] break-words whitespace-pre-wrap">
                            <span className="sr-only">
                                {withJosa(memberName(comment.memberKey), '이', '가')} 댓글:{' '}
                            </span>
                            {comment.body}
                        </p>
                    </div>
                </div>
            ))}
            {hiddenCount > 0 ? (
                <button
                    type="button"
                    onClick={() => setExpanded(true)}
                    className="min-h-touch self-start px-1 text-[13px] font-semibold text-brand"
                >
                    댓글 {hiddenCount}개 더 보기
                </button>
            ) : null}
            {typingMemberKey ? (
                <TypingIndicator memberKey={typingMemberKey} name={memberName(typingMemberKey)} />
            ) : null}
        </div>
    );
}

/** 리액션 줄 (DESIGN_SPEC 6장): 공감·위로·응원(아이콘 + 수). 내가 누른 것은 --color-brand */
export function ReactionBar({ reactions, onToggle, trailing = null }) {
    return (
        <div className="flex items-center gap-1">
            {REACTIONS.map(({ kind, label }) => {
                const Icon = REACTION_ICONS[kind];
                const pressed = reactions.mine.includes(kind);
                const count = reactions.counts[kind] ?? 0;
                return (
                    <button
                        key={kind}
                        type="button"
                        aria-pressed={pressed}
                        aria-label={`${label} ${count}`}
                        onClick={() => onToggle(kind)}
                        className={`flex h-9 items-center gap-1.5 rounded-full pr-2.5 pl-1.5 text-micro ${
                            pressed ? 'text-brand' : 'text-text-secondary'
                        }`}
                    >
                        <Icon size={18} strokeWidth={1.6} fill={pressed ? 'currentColor' : 'none'} aria-hidden="true" />
                        <span className="inline-flex items-center gap-1">
                            <span>{label}</span>
                            <span className="w-[2ch] text-right tabular-nums">{count}</span>
                        </span>
                    </button>
                );
            })}
            {trailing ? <span className="ml-auto text-micro font-normal text-label-muted">{trailing}</span> : null}
        </div>
    );
}

function DailyHeader({ item, label }) {
    return (
        <div className="flex items-center gap-2">
            <Avatar memberKey={item.memberKey} size={32} />
            <span className="text-[13px] font-semibold text-accent-text">{label}</span>
            <span className="text-[13px] text-text-secondary">
                {memberName(item.memberKey)} · {formatClock(item.publishedAt)}
            </span>
        </div>
    );
}

/**
 * 별채대화에 놓인 카드의 맨 아래 줄 (시안 03 README 5.5): 왼쪽 고정 문구 · 오른쪽 「상회칠판에서 보기 ›」.
 * 별채대화에서는 멤버 한 줄 댓글을 카드 안에 넣지 않습니다(카드 아래 받아치기로 올라옵니다, DEV_SPEC 4.6).
 */
function RoomFooter({ notice, onOpenNotice }) {
    return (
        <div className="flex items-center gap-2">
            {notice ? <span className="text-micro font-normal text-label-muted">{notice}</span> : null}
            <button
                type="button"
                onClick={onOpenNotice}
                className="ml-auto min-h-touch text-[13px] font-semibold text-brand"
            >
                상회칠판에서 보기 ›
            </button>
        </div>
    );
}

function CardFooter({ item, room, onReact, notice }) {
    if (room) {
        return (
            <>
                <ReactionBar reactions={item.reactions} onToggle={onReact} />
                <RoomFooter notice={notice} onOpenNotice={() => room.onOpenNotice(item)} />
            </>
        );
    }
    return (
        <>
            <MemberComments comments={itemComments(item)} />
            <ReactionBar reactions={item.reactions} onToggle={onReact} trailing={notice} />
        </>
    );
}

function itemComments(item) {
    return item.comment ? [{ key: `c${item.dailyItemsId}`, ...item.comment }] : [];
}

// ── 매일 콘텐츠 ─────────────────────────────────────────────

/**
 * 운세 글. 사주 정보가 없으면(공용 운세) 내 운세 자리에 「생일 알려주면 ○○ 것도 봐줄게」.
 * 유저가 누르는 것이라 횟수 제한이 없습니다 (DEV_SPEC 4.1).
 */
function FortuneCard({ item, displayName, hasBirth, onAskBirth, onReact, room, header }) {
    const payload = item.payload ?? {};
    const askBirth = item.common && !hasBirth;
    return (
        <Card as="article" aria-label="오늘의 운세" className="flex flex-col gap-3">
            {header ?? <DailyHeader item={item} label={DAILY_KINDS.fortune.name} />}
            {payload.headline ? <p className="m-0 font-serif text-title-2">{payload.headline}</p> : null}
            {askBirth ? (
                <div className="flex flex-col gap-2.5 rounded-[12px] bg-bg p-3">
                    <span className="text-label leading-[22px] font-normal text-text-secondary">
                        생일 알려주면 {displayName} 것도 봐줄게.
                    </span>
                    <Button variant="secondary" size="sm" className="self-start" onClick={onAskBirth}>
                        생일 알려주기
                    </Button>
                </div>
            ) : (
                <p className="m-0 font-serif text-body-member whitespace-pre-wrap">{payload.body ?? item.body}</p>
            )}
            {payload.move ? (
                <p className="m-0 text-[14px] leading-[22px] text-text-secondary">오늘 처신 · {payload.move}</p>
            ) : null}
            {payload.mention ? (
                <div className="flex items-center gap-2">
                    <Avatar memberKey={payload.mention.memberKey} size={20} />
                    <span className="font-serif text-[14px]">{payload.mention.body}</span>
                </div>
            ) : null}
            <CardFooter item={item} room={room} onReact={onReact} notice={DAILY_KINDS.fortune.notice} />
        </Card>
    );
}

function SentenceCard({ item, onReact, onOpenEvidence, room, header }) {
    const payload = item.payload ?? {};
    return (
        <Card as="article" aria-label="니체의 한 문장" className="flex flex-col gap-3">
            {header ?? <DailyHeader item={item} label={DAILY_KINDS.sentence.name} />}
            {payload.original ? (
                <span lang="de" className="text-caption text-text-secondary">
                    {payload.original}
                </span>
            ) : null}
            <p className="m-0 font-serif text-quote">{payload.translation ?? item.body}</p>
            {payload.gloss ? <p className="m-0 font-serif text-body-member">{payload.gloss}</p> : null}
            {item.evidence.map((chip) => (
                <EvidenceChip key={chip.key} label={chip.figure} onClick={() => onOpenEvidence(chip.key)} />
            ))}
            <CardFooter item={item} room={room} onReact={onReact} notice={null} />
        </Card>
    );
}

/** 오늘의 한 수 · {요일 제목}. 도 대표 글에는 「종목이나 코인은 추천하지 않아요.」 */
function DailyMoveCard({ item, onReact, onOpenEvidence, room, header }) {
    const label = `${DAILY_KINDS.daily_move.name} · ${item.title}`;
    return (
        <Card as="article" aria-label={label} className="flex flex-col gap-3">
            {header ?? <DailyHeader item={item} label={label} />}
            <p className="m-0 font-serif text-body-member whitespace-pre-wrap">{item.payload?.body ?? item.body}</p>
            {item.evidence.map((chip) => (
                <EvidenceChip key={chip.key} label={chip.figure} onClick={() => onOpenEvidence(chip.key)} />
            ))}
            <CardFooter
                item={item}
                room={room}
                onReact={onReact}
                notice={item.memberKey === 'do' ? NO_INVEST_NOTICE : null}
            />
        </Card>
    );
}

/** 일요일 이모의 주간 편지 (DEV_SPEC 4.14 4항, 08 B4: 본문 17/29, D54). 리액션은 다른 매일 콘텐츠와 같습니다 */
function WeeklyLetterCard({ item, onReact, room, header }) {
    return (
        <Card as="article" aria-label={WEEKLY_LETTER.name} className="flex flex-col gap-3">
            {header ?? <DailyHeader item={item} label={WEEKLY_LETTER.name} />}
            <p className="m-0 font-serif text-[17px] leading-[29px] whitespace-pre-wrap">
                {item.payload?.body ?? item.body}
            </p>
            <CardFooter item={item} room={room} onReact={onReact} notice={null} />
        </Card>
    );
}

/**
 * 체크인. 멤버 댓글 없이 유저 차례. 상회칠판에서는 「오늘 얘기 보내기」, 별채대화에서는 칩 그럭저럭 / 지쳤어 / 좋았어
 * (시안 03 README 5.9). 오늘 이미 답했으면 칩을 숨깁니다. 위기 뒤 24시간은 칩 없는 안부입니다.
 */
export const CHECKIN_CHIPS = ['그럭저럭', '지쳤어', '좋았어'];
/** 위기 뒤 24시간의 체크인: 칩 없는 이모의 안부 (DEV_SPEC 4.13, 시안 03 Q1) */
export const CALM_CHECKIN_TEXT = '오늘은 좀 어땠어. 말 안 해도 돼, 여기 있을게.';

function CheckinCard({ item, showButton, onStartDay, room }) {
    return (
        <Card
            as="article"
            aria-label={DAILY_KINDS.checkin.name}
            className="mt-2 flex flex-col items-center gap-3 px-4 py-5 text-center"
        >
            <Avatar memberKey={item.memberKey} size={40} />
            <span className="text-[13px] text-text-secondary">
                {memberName(item.memberKey)} · {formatClock(item.publishedAt)}
            </span>
            <p className="m-0 font-serif text-title-2">
                {room?.calm ? CALM_CHECKIN_TEXT : (item.payload?.body ?? item.body)}
            </p>
            {room && room.showCheckinChips ? (
                <div className="flex flex-wrap justify-center gap-2">
                    {CHECKIN_CHIPS.map((chip) => (
                        <button
                            key={chip}
                            type="button"
                            onClick={() => room.onCheckinChip(chip)}
                            className="h-10 rounded-[20px] border border-chip-line bg-surface px-4 text-[15px]"
                        >
                            {chip}
                        </button>
                    ))}
                </div>
            ) : null}
            {!room && showButton ? (
                <Button size="md" onClick={onStartDay}>
                    별채대화에서 답하기
                </Button>
            ) : null}
        </Card>
    );
}

/**
 * @param {object} [room]  별채대화에 놓일 때: { onOpenNotice(item), showCheckinChips, onCheckinChip(text) }
 * @param {import('react').ReactNode} [header]  상회칠판 칸이면 손글씨 제목 줄(시안 09 · 08 6.2)로 머리를 바꿉니다
 */
export function DailyItemCard({
    item,
    board,
    onStartDay,
    onAskBirth,
    onReact,
    onOpenEvidence,
    room = null,
    header = null,
}) {
    const react = (kind) => onReact(item, kind);
    if (item.kind === 'fortune') {
        return (
            <FortuneCard
                item={item}
                displayName={board.displayName}
                hasBirth={board.hasBirth}
                onAskBirth={onAskBirth}
                onReact={react}
                room={room}
                header={header}
            />
        );
    }
    if (item.kind === 'sentence') {
        return <SentenceCard item={item} onReact={react} onOpenEvidence={onOpenEvidence} room={room} header={header} />;
    }
    if (item.kind === 'daily_move') {
        return (
            <DailyMoveCard item={item} onReact={react} onOpenEvidence={onOpenEvidence} room={room} header={header} />
        );
    }
    if (item.kind === WEEKLY_LETTER.kind) {
        return <WeeklyLetterCard item={item} onReact={react} room={room} header={header} />;
    }
    if (item.kind === 'checkin') {
        return (
            <CheckinCard
                item={item}
                showButton={Boolean(board.canStartDay?.(item))}
                onStartDay={onStartDay}
                room={room}
            />
        );
    }
    return null;
}

// ── 내 글 ───────────────────────────────────────────────────

function FailedNotice({ onRetry, retrying }) {
    return (
        <div className="flex items-center gap-2 text-caption">
            <span className="text-error">댓글을 받지 못했어요.</span>
            <Button variant="text" size="sm" className="px-1" busy={retrying} onClick={onRetry}>
                다시 받기
            </Button>
        </div>
    );
}

function PostHeader({ kind, displayName, createdAt }) {
    return (
        <div className="flex items-center gap-2">
            <span
                aria-hidden="true"
                className="flex size-6 items-center justify-center rounded-full bg-brand text-micro font-bold text-on-brand"
            >
                {initialOf(displayName)}
            </span>
            <PostTag kind={kind} />
            <span className="ml-auto text-[13px] text-text-secondary">
                {displayName} · {formatClock(createdAt)}
            </span>
        </div>
    );
}

/**
 * 위기 신호가 애매한 글 (D17): 이모 한 명이 마음 쓰인다는 한마디를 남기고 1:1 로 부릅니다.
 * 게시판에서 묻고 답하지 않고, 안전 카드도 붙이지 않습니다. 하루·고민 모두 이 카드입니다.
 */
export function CheckInPostCard({ post, displayName, busy, onStart }) {
    const comments = post.replies.map((reply) => ({
        key: reply.repliesId,
        memberKey: reply.memberKey,
        body: reply.body,
    }));
    const answered = post.status === 'answered';
    return (
        <Card as="article" aria-label={post.kind === 'worry' ? '내 고민' : '내 하루'} className="flex flex-col gap-3">
            <PostHeader kind={post.kind} displayName={displayName} createdAt={post.createdAt} />
            <p className="m-0 text-body break-words whitespace-pre-wrap">{post.body}</p>
            <MemberComments comments={comments} typingMemberKey={nextResponderOf(post)} />
            {answered ? (
                <Button variant="secondary" size="md" className="self-start" busy={busy} onClick={onStart}>
                    이모랑 1:1로 얘기하기
                </Button>
            ) : null}
        </Card>
    );
}

/**
 * 고민 카드 (시안 03 README 5.8): ['고민' 칩 + 시간] → 원문 → [답할 멤버 아바타 24 + 상태 + 「답 n개 보기」].
 * 카드 전체가 버튼(→ 고민 스레드). 별채대화는 오른쪽 84%(RoomWorryCard), 상회칠판은 전체 폭(이 카드).
 */
export function WorryPostCard({ post, onOpen, onRetry, retrying }) {
    const header = (
        <span className="flex w-full items-center justify-between">
            <PostTag kind="worry" />
            <span className="text-[13px] text-text-secondary">{formatClock(post.createdAt)}</span>
        </span>
    );
    const bodyText = <span className="text-body break-words whitespace-pre-wrap">{post.body}</span>;

    if (post.status === 'failed') {
        return (
            <Card as="article" aria-label="내 고민" className="flex flex-col gap-3">
                {header}
                {bodyText}
                <FailedNotice onRetry={onRetry} retrying={retrying} />
            </Card>
        );
    }

    const status = worryCardStatus(post);
    const answered = new Set(post.answeredMemberKeys ?? []);
    return (
        <button
            type="button"
            onClick={onOpen}
            aria-label={`고민 스레드 열기: ${post.body.slice(0, 30)}`}
            className="flex w-full flex-col gap-3 rounded-card border border-hairline bg-surface p-4 text-left"
        >
            {header}
            {bodyText}
            <span className="flex w-full items-center gap-2">
                <span className="flex gap-1">
                    {(post.responderKeys ?? []).map((memberKey) => (
                        <span key={memberKey} className={answered.has(memberKey) ? '' : 'opacity-45'}>
                            <Avatar memberKey={memberKey} size={24} />
                        </span>
                    ))}
                </span>
                <span className="min-w-0 flex-1 text-[13px] text-text-secondary">{status.text}</span>
                {status.link ? (
                    <span className="shrink-0 text-[13px] font-semibold text-brand">{status.link}</span>
                ) : null}
            </span>
        </button>
    );
}
